import { logger } from 'Services/logger/logger';
import type { User } from 'Types/user';
import { convertToError } from 'Utilities/error/convertToError';

import type { ConfigDatafile } from './optimizely';
import { getUserAttributesPayload } from './optimizely';
import { OPTIMIZELY_TO_FE_FEATURE_FLAGS_MAP, OPTIMIZELY_VARIANT_FLAGS_MAP } from './optimizely.consts';
import { getServerOptimizelyClient } from './serverOptimizely';

const SCOPE = 'extractOptimizelyFeatureFlags';
const DEFAULT_VARIANT_DECISION = 'off';

export const extractOptimizelyFeatureFlags = async (
	user?: User,
	datafile?: ConfigDatafile,
): Promise<Record<string, boolean | string>> => {
	if (!user) return {};

	try {
		const client = await getServerOptimizelyClient(datafile);
		if (!client) return {};

		const userId = String(user.id);
		const attributes = getUserAttributesPayload({
			dealerContactId: user.id,
			dealershipId: user.dealerId,
		});

		const userContext = client.createUserContext(userId, attributes);
		if (!userContext) return {};

		const booleanKeys = Object.keys(OPTIMIZELY_TO_FE_FEATURE_FLAGS_MAP);
		const variantKeys = Object.keys(OPTIMIZELY_VARIANT_FLAGS_MAP);
		const allKeys = [...booleanKeys, ...variantKeys];

		if (allKeys.length === 0) return {};

		const decisions = userContext.decideForKeys(allKeys);
		const result: Record<string, boolean | string> = {};

		for (const [experimentId, { feToggleName }] of Object.entries(OPTIMIZELY_TO_FE_FEATURE_FLAGS_MAP)) {
			result[feToggleName] = decisions[experimentId]?.enabled ?? false;
		}

		for (const [experimentId, { feToggleName }] of Object.entries(OPTIMIZELY_VARIANT_FLAGS_MAP)) {
			result[feToggleName] = `${decisions[experimentId]?.variationKey ?? DEFAULT_VARIANT_DECISION}`;
		}

		return result;
	} catch (e) {
		const error = convertToError(e);
		logger.error({ error, message: error.message, scope: SCOPE });
		return {};
	}
};
